import { type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getFeatureFlags, type AppConfigRoot } from '@a1/shared';
import { aiFeaturesEnabled } from 'constants-ts/featureFlags';
import FeatureFlagsContext from './FeatureFlagsContext';

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

/**
 * Single Redux subscriber for feature flags. Mount once near the top of the authenticated
 * layout so all descendants can read flags via use(FeatureFlagsContext) without each
 * needing their own selector.
 *
 * REMINDER: Split Contexts and Providers into separate files to allow for hot-reloading of Providers without affecting Context consumers!
 */
const FeatureFlagsProvider = ({ children }: FeatureFlagsProviderProps) => {
  const appConfig = useSelector((state: AppConfigRoot) => state.appConfig?.data);
  const flags = useMemo(() => getFeatureFlags(appConfig, { aiFeaturesEnabled }), [appConfig]);

  return <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>;
};

export default FeatureFlagsProvider;
